import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import Picture from './Picture';
import './homepage.css';
import './homepage-content.css';
import './homepage-splash.css';

// Hero slideshow images
const HERO_SLIDES = [
  '/images/hero-1.jpg',
  '/images/hero-2.jpg',
  '/images/hero-3.jpg',
];

// Feature data with images
const FEATURES = [
  {
    image: '/images/feature-route.jpg',
    title: '最適ルート自動計算',
    desc: 'Google Maps APIと独自の最適化アルゴリズムを組み合わせ、最短・最効率の送迎ルートを自動で算出。ドライバーの負担を軽減し、燃料コストの大幅な削減を実現します。',
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
  },
  {
    image: '/images/feature-management.jpg',
    title: 'キャスト・ドライバー管理',
    desc: '帰宅リクエストの受付からドライバーへの配車まで、すべてを一つのダッシュボードで一元管理。リアルタイムのステータス確認で、現場の状況を瞬時に把握できます。',
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    image: '/images/feature-notification.jpg',
    title: 'リアルタイム通知',
    desc: 'ルート確定・変更・ドライバー到着など、あらゆるステータス更新をリアルタイムでプッシュ通知。キャストもドライバーも、必要な情報を見逃しません。',
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    image: '/images/feature-multistore.jpg',
    title: '系列店一括管理',
    desc: '複数店舗のルート管理を系列グループ単位で統合。店舗間のリソースを最適に配分し、グループ全体のオペレーション効率を飛躍的に向上させます。',
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

// Particle canvas for hero background
const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.4;
        this.speedY = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.5 + 0.1;
        this.color = Math.random() > 0.5
          ? `rgba(201, 169, 97, ${this.opacity})`
          : `rgba(157, 78, 221, ${this.opacity * 0.6})`;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 12000));
    for (let i = 0; i < count; i++) {
      particles.push(new Particle());
    }

    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(201, 169, 97, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      drawLines();
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas ref={canvasRef} className="homepage-particle-canvas" />
  );
};

// Count-up hook & StatCounter - 契約店舗・実績が増えたら復活させる
/*
const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  const start = useCallback(() => {
    if (started) return;
    setStarted(true);
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, started]);

  return { count, ref, start };
};

const StatCounter = ({ value, suffix, label }) => {
  const { count, ref, start } = useCountUp(value, 2200);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { start(); observer.disconnect(); } },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [start, ref]);

  return (
    <div className="homepage-stat" ref={ref}>
      <span className="homepage-stat-number">{count.toLocaleString()}{suffix}</span>
      <span className="homepage-stat-label">{label}</span>
    </div>
  );
};
*/

// Hero slideshow component
const HeroSlideshow = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="hp-slideshow">
      {HERO_SLIDES.map((src, i) => (
        <div
          key={i}
          className={`hp-slideshow-slide ${i === current ? 'hp-slide-active' : ''}`}
          style={{ backgroundImage: `url(${src})` }}
        />
      ))}
    </div>
  );
};

// Splash screen component
const SplashScreen = ({ onFinish }) => {
  const [phase, setPhase] = useState('enter'); // enter -> glow -> exit

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase('glow'), 800),
      setTimeout(() => setPhase('exit'), 2400),
      setTimeout(() => onFinish(), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onFinish]);

  return (
    <div className={`splash-screen splash-${phase}`}>
      {/* Road light trails */}
      <div className="splash-road">
        <div className="splash-road-line splash-road-line-1" />
        <div className="splash-road-line splash-road-line-2" />
        <div className="splash-road-line splash-road-line-3" />
        <div className="splash-road-line splash-road-line-4" />
        <div className="splash-road-line splash-road-line-5" />
      </div>
      {/* Headlight glow */}
      <div className="splash-glow" />
      {/* Logo */}
      <div className="splash-logo-wrap">
        <img
          src="/images/logo_trim.png"
          alt="送ルート"
          className="splash-logo"
        />
      </div>
      {/* Bottom tagline */}
      <p className="splash-tagline">キャバクラ送り業務特化Webアプリケーション</p>
    </div>
  );
};

const HomePage = () => {
  const [splashDone, setSplashDone] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  const handleSplashFinish = useCallback(() => {
    setSplashDone(true);
  }, []);

  useEffect(() => {
    document.body.classList.add('homepage-active');
    // Delay hero animation until splash is done
    if (splashDone) {
      const timer = setTimeout(() => setHeroVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [splashDone]);

  useEffect(() => {
    return () => document.body.classList.remove('homepage-active');
  }, []);

  // Header background on scroll
  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-triggered fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hp-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const els = document.querySelectorAll('.hp-fade-in');
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [contactForm, setContactForm] = useState({
    name: '', email: '', company: '', subject: '', message: ''
  });
  const [contactStatus, setContactStatus] = useState('');
  const [contactSending, setContactSending] = useState(false);

  useEffect(() => {
    publicAPI.getAnnouncements().then(res => {
      setAnnouncements(res.data.announcements || []);
    }).catch(() => {
      // Fallback: show nothing if API fails
    });
  }, []);

  // Re-observe dynamically added fade-in elements when announcements load
  useEffect(() => {
    if (announcements.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hp-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const els = document.querySelectorAll('.homepage-announcement-item.hp-fade-in:not(.hp-visible), .homepage-announcements ~ .hp-fade-in:not(.hp-visible)');
    els.forEach(el => observer.observe(el));
    // Also observe the section title above announcements
    const title = document.querySelector('.homepage-section-alt .homepage-section-title.hp-fade-in:not(.hp-visible)');
    if (title) observer.observe(title);
    return () => observer.disconnect();
  }, [announcements]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      setContactStatus('error:全ての必須項目を入力してください');
      return;
    }
    try {
      setContactSending(true);
      await publicAPI.submitInquiry(
        contactForm.name, contactForm.email, contactForm.company,
        contactForm.subject, contactForm.message
      );
      setContactStatus('success');
      setContactForm({ name: '', email: '', company: '', subject: '', message: '' });
    } catch {
      setContactStatus('error:送信に失敗しました。しばらく経ってから再度お試しください。');
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="homepage">
      {/* Splash Screen */}
      {!splashDone && <SplashScreen onFinish={handleSplashFinish} />}

      {/* Header */}
      <header className={`homepage-header ${headerScrolled ? 'hp-header-scrolled' : ''}`}>
        <div className="homepage-header-content">
          <div className="homepage-header-brand">
            <img
              src="/images/logo_orange.png"
              alt="送ルート"
              className="homepage-logo"
            />
            <img
              src="/images/title_white_trim.png"
              alt="送ルート"
              className="homepage-brand-title"
            />
          </div>
          <Link to="/login" className="homepage-login-btn">
            ログイン
          </Link>
        </div>
      </header>

      {/* Hero Section with Slideshow */}
      <section className="homepage-hero">
        <HeroSlideshow />
        <ParticleCanvas />
        <div className="homepage-hero-overlay" />
        {/* Floating accent orbs */}
        <div className="hp-orb hp-orb-1" />
        <div className="hp-orb hp-orb-2" />
        <div className="hp-orb hp-orb-3" />

        <div className={`homepage-hero-content ${heroVisible ? 'hp-hero-enter' : ''}`}>
          <img
            src="/images/title_white_trim.png"
            alt="送ルート"
            className="homepage-hero-logo"
          />
          <p className="homepage-hero-subtitle">
            キャバクラ送り業務特化Webアプリケーション
          </p>
          <p className="homepage-hero-description">
            キャスト送迎の配車・ルート計算を自動化し、<br />
            ドライバーの負担軽減とコスト削減を実現します。
          </p>
          <Link to="/login" className="homepage-hero-cta">
            <span>システムにログイン</span>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Stats Section - 契約店舗・実績が増えたら実数値で復活させる */}
      {/*
      <section className="homepage-stats-bar">
        <div className="homepage-container">
          <div className="homepage-stats-grid">
            <StatCounter value={50} suffix="%" label="コスト削減率" />
            <StatCounter value={98} suffix="%" label="稼働安定率" />
            <StatCounter value={500} suffix="+" label="ルート計算実績" />
            <StatCounter value={24} suffix="/7" label="サポート体制" />
          </div>
        </div>
      </section>
      */}

      {/* Features Section - Alternating Layout */}
      <section className="homepage-section">
        <div className="homepage-container">
          <h2 className="homepage-section-title hp-fade-in">送ルートの特徴</h2>
          <div className="homepage-features-list">
            {FEATURES.map((feature, i) => (
              <div
                key={i}
                className={`hp-feature-row hp-fade-in ${i % 2 === 1 ? 'hp-feature-row-reverse' : ''}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="hp-feature-image-wrap">
                  <Picture
                    src={feature.image}
                    alt={feature.title}
                    className="hp-feature-image"
                    loading="lazy"
                  />
                </div>
                <div className="hp-feature-text">
                  <div className="hp-feature-text-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements Section */}
      {announcements.length > 0 && (
        <section className="homepage-section homepage-section-alt">
          <div className="homepage-container">
            <h2 className="homepage-section-title hp-fade-in">運営からのお知らせ</h2>
            <div className="homepage-announcements">
              {announcements.map((item, i) => (
                <div
                  key={item.announcement_id}
                  className="homepage-announcement-item hp-fade-in"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="homepage-announcement-meta">
                    <span className="homepage-announcement-date">
                      {new Date(item.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.')}
                    </span>
                    <span className="homepage-announcement-tag">{item.tag}</span>
                  </div>
                  <p className="homepage-announcement-title">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Form Section */}
      <section className="homepage-section" id="contact">
        <div className="homepage-container">
          <h2 className="homepage-section-title hp-fade-in">お問い合わせ</h2>
          <div className="hp-fade-in" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {contactStatus === 'success' ? (
              <div style={{
                textAlign: 'center', padding: '3rem 1rem',
                background: 'rgba(72, 187, 120, 0.1)', borderRadius: '16px', border: '1px solid rgba(72, 187, 120, 0.3)'
              }}>
                <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#10003;</p>
                <h3 style={{ marginBottom: '0.5rem' }}>お問い合わせを受け付けました</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)' }}>担当者より折り返しご連絡いたします。</p>
                <button
                  onClick={() => setContactStatus('')}
                  style={{
                    marginTop: '1rem', padding: '0.5rem 1.5rem',
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '8px', color: '#fff', cursor: 'pointer'
                  }}
                >
                  新しいお問い合わせを送る
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} style={{ display: 'grid', gap: '1rem' }}>
                {contactStatus.startsWith('error:') && (
                  <div style={{
                    padding: '0.75rem', borderRadius: '8px',
                    background: 'rgba(245, 101, 101, 0.15)', border: '1px solid rgba(245, 101, 101, 0.3)',
                    color: '#fc8181', fontSize: '0.9rem'
                  }}>
                    {contactStatus.replace('error:', '')}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>お名前 *</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="山田 太郎"
                      required
                      style={{
                        width: '100%', padding: '0.75rem', borderRadius: '8px',
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                        color: '#fff', fontSize: '0.95rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>メールアドレス *</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="example@company.com"
                      required
                      style={{
                        width: '100%', padding: '0.75rem', borderRadius: '8px',
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                        color: '#fff', fontSize: '0.95rem'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>会社名</label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                    placeholder="株式会社〇〇"
                    style={{
                      width: '100%', padding: '0.75rem', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                      color: '#fff', fontSize: '0.95rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>件名 *</label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="お問い合わせの件名"
                    required
                    style={{
                      width: '100%', padding: '0.75rem', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                      color: '#fff', fontSize: '0.95rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>メッセージ *</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="お問い合わせ内容をご記入ください"
                    required
                    rows={5}
                    style={{
                      width: '100%', padding: '0.75rem', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                      color: '#fff', fontSize: '0.95rem', resize: 'vertical'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={contactSending}
                  style={{
                    padding: '0.85rem 2rem', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #f6ad55, #ed8936)',
                    border: 'none', color: '#1a202c', fontWeight: 'bold',
                    fontSize: '1rem', cursor: contactSending ? 'not-allowed' : 'pointer',
                    opacity: contactSending ? 0.7 : 1, transition: 'opacity 0.2s'
                  }}
                >
                  {contactSending ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </div>
        </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="homepage-container">
          <div className="homepage-footer-grid">
            <div className="homepage-footer-brand">
              <img
                src="/images/logo_orange.png"
                alt="送ルート"
                className="homepage-footer-logo"
              />
              <span className="homepage-footer-brand-name">送ルート</span>
              <p className="homepage-footer-tagline">キャバクラ送り業務特化Webアプリケーション</p>
            </div>
            <div className="homepage-footer-links">
              <h4>サービス</h4>
              <ul>
                <li><Link to="/login">ログイン</Link></li>
              </ul>
            </div>
            <div className="homepage-footer-links">
              <h4>サポート</h4>
              <ul>
                <li><a href="#contact">お問い合わせ</a></li>
              </ul>
            </div>
            <div className="homepage-footer-links">
              <h4>法的情報</h4>
              <ul>
                <li><a href="/company">会社概要</a></li>
                <li><a href="/privacy">プライバシーポリシー</a></li>
                <li><a href="/terms">利用規約</a></li>
              </ul>
            </div>
          </div>
          <div className="homepage-footer-bottom">
            <p>&copy; 2026 送ルート All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
